import type { SequencingPlatformKey } from '../Generated/SequencingPlatform.ts'
import { SequencingPlatforms } from '../Generated/SequencingPlatform.ts'
import type { SampleConfigurationTypeKey } from '../Generated/SampleConfigurationType.ts'
import { SampleConfigurationTypes } from '../Generated/SampleConfigurationType.ts'
import type { ProcessingTypeKey } from '../Generated/ProcessingType.ts'
import { ProcessingTypes } from '../Generated/ProcessingType.ts'
import type { DetectedSequencingPlatform } from './sequencingPlatformDetection.ts'
import type { IOrder } from '../Model/Order/IOrder.ts'
import type { ISamplePool } from '../Model/SamplePool/ISamplePool.ts'
import type { ValidationIssue } from '../Model/Order/ValidationIssue.ts'
import type { SequencingPlatformPreview } from '../Generated/SequencingPlatformPreview.ts'

/**
 * The order's real answer to "which pipeline runs this": a lab override always wins over what was
 * detected from the files, and either can be absent (nothing uploaded / never overridden).
 */
export const getEffectiveSequencingPlatform = (
  detected?: SequencingPlatformKey | null,
  override?: SequencingPlatformKey | null
): SequencingPlatformKey | null => override ?? detected ?? null

/**
 * The submit has no platform to route on and none of the files named one (SRA re-exports strip the
 * headers detection reads), so the lab must choose: the pipeline would otherwise fall back to Illumina.
 * Mixed platforms carry their own issue, which a choice does not resolve.
 */
export const needsSequencingPlatformChoice = (
  preview: Pick<SequencingPlatformPreview, 'detectedPlatforms' | 'effectiveSequencingPlatform'> | null | undefined
): boolean => !!preview && !preview.effectiveSequencingPlatform && (preview.detectedPlatforms ?? []).length === 0

/**
 * The order stores a detected platform only once submitted. Before that its live samples decide, the way
 * the submit will: one platform among them is the answer, none or several is no answer.
 */
export const getDetectedSequencingPlatform = (
  order: Pick<IOrder, 'id' | 'orderSubmission' | 'detectedSequencingPlatform'>,
  samplePools: ISamplePool[] | 'LOADING' | undefined
): SequencingPlatformKey | null => {
  if (order.orderSubmission) {
    return order.detectedSequencingPlatform ?? null
  }
  if (!samplePools || samplePools === 'LOADING') {
    return null
  }
  const platforms = new Set(
    samplePools
      .filter((pool) => pool.orderId === order.id)
      .flatMap((pool) => [...(pool.samples ?? []), ...(pool.negativeControls ?? [])])
      .flatMap((item) => (item.detectedSequencingPlatform ? [item.detectedSequencingPlatform] : []))
  )
  return platforms.size === 1 ? [...platforms][0] : null
}

/** No file names its platform and the lab has not chosen one: submitting would silently run it as Illumina. */
export const isSequencingPlatformUnknown = (
  order: Pick<IOrder, 'id' | 'orderSubmission' | 'detectedSequencingPlatform' | 'sequencingPlatformOverride'>,
  samplePools: ISamplePool[] | 'LOADING' | undefined
): boolean => {
  if (order.orderSubmission || order.sequencingPlatformOverride || !samplePools || samplePools === 'LOADING') {
    return false
  }
  return !samplePools
    .filter((pool) => pool.orderId === order.id)
    .some((pool) => [...(pool.samples ?? []), ...(pool.negativeControls ?? [])].some((item) => !!item.detectedSequencingPlatform))
}

const sequencingPlatformBadgeStyle: Record<SequencingPlatformKey, string> = {
  ILLUMINA: 'bg-slate-100 text-slate-700 border border-slate-200',
  ONT: 'bg-indigo-100 text-indigo-800 border border-indigo-200'
}

/** No badge classes for `null` — callers should render nothing rather than a guessed default. */
export const getSequencingPlatformInlineBadgeStyle = (platform?: SequencingPlatformKey | null): string =>
  platform ? sequencingPlatformBadgeStyle[platform] : ''

export const getSequencingPlatformLabel = (platform?: SequencingPlatformKey | null): string | null =>
  platform ? SequencingPlatforms[platform].label : null

const earlyAccessSequencingPlatforms: ReadonlySet<SequencingPlatformKey> = new Set(['ONT'])

export const isEarlyAccessSequencingPlatform = (platform?: SequencingPlatformKey | null): boolean =>
  !!platform && earlyAccessSequencingPlatforms.has(platform)

/** Shown next to the platform badge wherever it appears, so the status outlives the submit notice. */
export const getSequencingPlatformEarlyAccessTag = (platform?: SequencingPlatformKey | null): string | null =>
  isEarlyAccessSequencingPlatform(platform) ? 'Early access' : null

export const sequencingPlatformSettingLabel = 'Sequencing platform'

export const sequencingPlatformAutoDetectedText = 'Auto-detected from your files (Illumina / Nanopore)'

export const sequencingPlatformSettingTooltip =
  'We read the platform from your FASTQ files when you upload them, so there is nothing to choose here. Nanopore support is early access.'

export const sequencingPlatformDropzoneHint = 'Illumina or Nanopore FASTQ: the platform is detected automatically.'

/** `null` means unrestricted (e.g. Illumina) — every `SampleConfigurationTypeKey` is valid. */
export const getAllowedSampleConfigurationsForPlatform = (platform?: SequencingPlatformKey | null): SampleConfigurationTypeKey[] | null => {
  if (!platform) return null
  return SequencingPlatforms[platform].allowedSampleConfigurations ?? null
}

export const isSampleConfigurationAllowedForPlatform = (
  config: SampleConfigurationTypeKey,
  platform?: SequencingPlatformKey | null
): boolean => {
  const allowed = getAllowedSampleConfigurationsForPlatform(platform)
  return allowed === null || allowed.includes(config)
}

/** `null` platform (nothing uploaded yet) constrains nothing. Mirrors `SequencingPlatform.supports(ProcessingType)`. */
export const isProcessingTypeAllowedForPlatform = (processingType: ProcessingTypeKey, platform?: SequencingPlatformKey | null): boolean => {
  const allowed = platform ? SequencingPlatforms[platform].allowedProcessingTypes : undefined
  return !allowed || allowed.includes(processingType)
}

/** Same wording as the backend's refusal, so the warning and the rejected submit read alike. */
export const getUnsupportedProcessingTypeMessage = (
  platform: SequencingPlatformKey | null | undefined,
  processingType: ProcessingTypeKey
): string | null => {
  if (!platform || isProcessingTypeAllowedForPlatform(processingType, platform)) return null
  const allowedLabels = (SequencingPlatforms[platform].allowedProcessingTypes ?? []).map((key) => ProcessingTypes[key].label)
  return `${SequencingPlatforms[platform].label} data can only be processed as ${allowedLabels.join(' or ')}, not ${ProcessingTypes[processingType].label}.`
}

type PlatformAndProcessingType = {
  detectedSequencingPlatform?: SequencingPlatformKey | null
  sequencingPlatformOverride?: SequencingPlatformKey | null
  processingType: ProcessingTypeKey
}

/** Why this order cannot be submitted as configured, judged on the effective platform; `null` when it can. */
export const getPlatformProcessingTypeConflict = (order: PlatformAndProcessingType): string | null => {
  const platform = getEffectiveSequencingPlatform(order.detectedSequencingPlatform, order.sequencingPlatformOverride)
  const reason = getUnsupportedProcessingTypeMessage(platform, order.processingType)
  if (!reason) return null
  const allowed = (platform && SequencingPlatforms[platform].allowedProcessingTypes) || []
  const target = allowed.map((key) => ProcessingTypes[key].label).join(' or ')
  return `${reason} This order cannot be submitted until its processing type is changed to ${target} (Edit order).`
}

/**
 * The client's explanation from the rule it shares with the backend, for a refusal that arrives without
 * the backend's typed issue. `null` when the rule would not have refused, i.e. the 400 had another cause.
 */
export const explainPlatformOverrideRefusal = (order: PlatformAndProcessingType, platform: SequencingPlatformKey | null): string | null =>
  getUnsupportedProcessingTypeMessage(platform ?? order.detectedSequencingPlatform, order.processingType)

/** As {@link explainPlatformOverrideRefusal}; the backend only checks a processing type that changes. */
export const explainProcessingTypeRefusal = (order: PlatformAndProcessingType, processingType: ProcessingTypeKey): string | null =>
  processingType === order.processingType
    ? null
    : getUnsupportedProcessingTypeMessage(
        getEffectiveSequencingPlatform(order.detectedSequencingPlatform, order.sequencingPlatformOverride),
        processingType
      )

const mergedConfigKeys: SampleConfigurationTypeKey[] = ['MERGED_SAMPLES', 'MERGED_AND_TRIMMED_SAMPLES']

/**
 * True when a platform's allowed set is exactly the two merged variants, i.e. the only remaining
 * question is whether primers were already stripped — a fit for a single yes/no control rather than a
 * dropdown. Derived from the generated allowlist, not hardcoded to `ONT`, so it keeps tracking whatever
 * the backend enum says.
 */
export const usesPrimersRemovedToggle = (platform?: SequencingPlatformKey | null): boolean => {
  const allowed = getAllowedSampleConfigurationsForPlatform(platform)
  return !!allowed && allowed.length === mergedConfigKeys.length && mergedConfigKeys.every((key) => allowed.includes(key))
}

export const isPrimersRemovedSampleConfiguration = (config: SampleConfigurationTypeKey): boolean => config === 'MERGED_AND_TRIMMED_SAMPLES'

/** Our ONT primer trimming is still being tuned, so reads trimmed before upload are the safer input. */
const recommendedSampleConfiguration: Partial<Record<SequencingPlatformKey, SampleConfigurationTypeKey>> = {
  ONT: 'MERGED_AND_TRIMMED_SAMPLES'
}

export const isRecommendedSampleConfiguration = (config: SampleConfigurationTypeKey, platform?: SequencingPlatformKey | null): boolean =>
  !!platform && recommendedSampleConfiguration[platform] === config

export const getMergedSampleConfigurationForPrimersRemoved = (primersRemoved: boolean): SampleConfigurationTypeKey =>
  primersRemoved ? 'MERGED_AND_TRIMMED_SAMPLES' : 'MERGED_SAMPLES'

/* The stored value stays MERGED_SAMPLES / MERGED_AND_TRIMMED_SAMPLES; the Illumina labels name a merge step that never ran on Nanopore reads. */
const ontSampleConfigurationLabels: Partial<Record<SampleConfigurationTypeKey, string>> = {
  MERGED_SAMPLES: 'Basecalled - ONT',
  MERGED_AND_TRIMMED_SAMPLES: 'Basecalled Trimmed - ONT'
}

export interface InputShape {
  platform: SequencingPlatformKey | null
  pairedEnd: boolean
  multiplexed: boolean
  primersPresent: boolean
}

/** The input in a reader's words; the explanation that sits beside an entry point's name. */
export const describeInputShape = ({ platform, pairedEnd, multiplexed, primersPresent }: InputShape): string => {
  const files = multiplexed
    ? 'Paired-end FASTQ pairs holding several inline-tagged samples'
    : pairedEnd
      ? 'One paired-end FASTQ pair per sample'
      : platform === 'ILLUMINA'
        ? 'One single-read FASTQ file per sample'
        : 'One FASTQ file per sample'
  const primers = primersPresent ? 'primers still present' : 'primers already removed'
  const suffix = platform === 'ONT' ? ' (Nanopore)' : platform === 'ILLUMINA' ? ' (Illumina)' : ''
  return `${files}, ${primers}${suffix}`
}

/** Explanation for a configuration as offered in a menu; platform-neutral wording while none is known. */
export const describeSampleConfiguration = (config: SampleConfigurationTypeKey, platform?: SequencingPlatformKey | null): string => {
  const { impliesMerged, impliesTrimmed } = SampleConfigurationTypes[config]
  return describeInputShape({
    platform: platform ?? null,
    pairedEnd: (platform ? SequencingPlatforms[platform].isPairedEnd : true) && !impliesMerged,
    multiplexed: config === 'COMBINED_SAMPLE_PAIRS',
    primersPresent: !impliesTrimmed
  })
}

const illuminaSampleConfigurationLabels: Partial<Record<SampleConfigurationTypeKey, string>> = {
  MERGED_SAMPLES: 'Single-Strand - Illumina',
  MERGED_AND_TRIMMED_SAMPLES: 'Single-Strand Trimmed - Illumina'
}

/** The generated labels name both platforms on the single-file configs; a known platform narrows them. */
export const getSampleConfigurationLabel = (config: SampleConfigurationTypeKey, platform?: SequencingPlatformKey | null): string => {
  if (usesPrimersRemovedToggle(platform)) {
    return ontSampleConfigurationLabels[config] ?? SampleConfigurationTypes[config].label
  }
  if (platform === 'ILLUMINA') {
    return illuminaSampleConfigurationLabels[config] ?? SampleConfigurationTypes[config].label
  }
  return SampleConfigurationTypes[config].label
}

const platformOverrideConsequence: Record<SequencingPlatformKey, string> = {
  ONT: 'skip dereplication and call consensus sequences instead',
  ILLUMINA: 'run per-read dereplication, which assumes an error rate Nanopore reads do not have'
}

/**
 * Names the actual detected platform and sample count rather than a generic are-you-sure — an override
 * that silently disagrees with the evidence is exactly the mistake this warns about. `null` when there
 * is nothing to warn about (no detection yet, or the override agrees with it).
 */
export const getSequencingPlatformOverrideWarning = (
  detected: SequencingPlatformKey | null | undefined,
  overrideTo: SequencingPlatformKey,
  sampleCount: number
): string | null => {
  if (!detected || detected === overrideTo) return null
  const detectedLabel = SequencingPlatforms[detected].label
  const targetLabel = SequencingPlatforms[overrideTo].label
  const sampleWord = sampleCount === 1 ? 'sample' : 'samples'
  const subject = sampleCount === 1 ? 'It' : 'They'
  const object = sampleCount === 1 ? 'it' : 'them'
  return (
    `${sampleCount} ${sampleWord} look${sampleCount === 1 ? 's' : ''} like ${detectedLabel}. ` +
    `Running ${object} through the ${targetLabel} pipeline will ${platformOverrideConsequence[overrideTo]} — ` +
    `if ${subject.toLowerCase()} really ${sampleCount === 1 ? 'is' : 'are'} ${detectedLabel}, the results will be wrong, not just different.`
  )
}

/**
 * Why a sample-configuration switch the detected platform calls for cannot be offered. These are the
 * frontend's own names for the three conditions an order-update refuses the switch on; a fourth reason
 * cannot arise here, because a mismatch by definition means the target differs from the current value.
 */
export type SampleConfigurationSwitchBlocker = 'order-submitted' | 'multiplexed-samples' | 'samples-present'

export type SampleConfigurationMismatch = {
  detected: SequencingPlatformKey
  current: SampleConfigurationTypeKey
  /** The configurations the detected platform does allow, the recommended one first, then untrimmed first. Never empty. */
  targets: SampleConfigurationTypeKey[]
  sampleCount: number
  /** How many of them are R1/R2 pairs — only those stop a paired order from turning merged. */
  pairedSampleCount: number
  /** `null` when the switch can be offered; otherwise what stands in its way. */
  blocker: SampleConfigurationSwitchBlocker | null
}

const recommendedThenUntrimmedFirst =
  (platform: SequencingPlatformKey) =>
  (a: SampleConfigurationTypeKey, b: SampleConfigurationTypeKey): number =>
    Number(isRecommendedSampleConfiguration(b, platform)) - Number(isRecommendedSampleConfiguration(a, platform)) ||
    Number(SampleConfigurationTypes[a].impliesTrimmed) - Number(SampleConfigurationTypes[b].impliesTrimmed)

/**
 * The correction an upload calls for: files were sniffed as a platform whose allowed configurations do
 * not include the one the order is set to. `null` means there is nothing to say — no verdict, or the
 * order is already configured for what was detected.
 *
 * Detection never writes the samples' own flags; it can only ask for the *order's* configuration to
 * change, which is what already drives them.
 */
export const getSampleConfigurationMismatch = (order: {
  detected?: DetectedSequencingPlatform | null
  current: SampleConfigurationTypeKey
  sampleCount: number
  /** Defaults to `sampleCount`, i.e. every sample counted as a pair. */
  pairedSampleCount?: number
  submitted: boolean
}): SampleConfigurationMismatch | null => {
  const { detected, current, sampleCount, submitted } = order
  const pairedSampleCount = order.pairedSampleCount ?? sampleCount
  if (!detected || detected === 'UNKNOWN') return null
  if (isSampleConfigurationAllowedForPlatform(current, detected)) return null
  const allowed = getAllowedSampleConfigurationsForPlatform(detected)
  if (!allowed || allowed.length === 0) return null

  /* merged to paired is always possible; paired to merged only while no sample is an R1/R2 pair */
  const reachable =
    SampleConfigurationTypes[current].impliesMerged || pairedSampleCount === 0
      ? allowed
      : allowed.filter((target) => !SampleConfigurationTypes[target].impliesMerged)

  const blocker: SampleConfigurationSwitchBlocker | null = submitted
    ? 'order-submitted'
    : current === 'COMBINED_SAMPLE_PAIRS'
      ? 'multiplexed-samples'
      : reachable.length === 0
        ? 'samples-present'
        : null

  const targets = [...(blocker ? allowed : reachable)].sort(recommendedThenUntrimmedFirst(detected))
  return { detected, current, targets, sampleCount, pairedSampleCount, blocker }
}

/**
 * Names the evidence, the consequence of leaving it, and the one way forward — the register the
 * platform-override warning uses. An offer says what the switch costs (nothing, on an empty order); a
 * blocked mismatch says which way out is left instead.
 */
export const getSampleConfigurationMismatchMessage = (mismatch: SampleConfigurationMismatch): string => {
  const platformLabel = SequencingPlatforms[mismatch.detected].label
  const lead =
    `These files look like ${platformLabel} reads, but this order is set to ` +
    `"${getSampleConfigurationLabel(mismatch.current, null)}", which they cannot satisfy — ` +
    'as it stands the order will be refused at submission.'

  switch (mismatch.blocker) {
    case 'order-submitted':
      return `${lead} The order is already submitted and its pipeline chosen, so the sample format is frozen. Upload this data into a new order.`
    case 'multiplexed-samples':
      return (
        `${lead} Splitting several samples out of one pair of files is a paired step ${platformLabel} data cannot go ` +
        'through, and that format cannot be switched to any other. Upload this data into a new order.'
      )
    case 'samples-present': {
      const count = mismatch.pairedSampleCount
      return (
        `${lead} ${count} sample${count === 1 ? '' : 's'} already uploaded to this order ${count === 1 ? 'is an' : 'are'} ` +
        `R1/R2 pair${count === 1 ? '' : 's'}, which ${platformLabel} samples cannot be, so the format cannot be switched here. ` +
        `Delete those samples first, or upload this ${platformLabel} data into a new order.`
      )
    }
    case null:
      return (
        lead +
        (mismatch.sampleCount === 0
          ? ' No samples have been uploaded yet, so the format can still be switched — nothing gets re-labelled and nothing has to be uploaded again.'
          : ` None of the ${mismatch.sampleCount} sample${mismatch.sampleCount === 1 ? '' : 's'} already uploaded is an R1/R2 pair, ` +
            'so the format can still be switched — they are re-labelled, and nothing has to be uploaded again.') +
        (usesPrimersRemovedToggle(mismatch.detected)
          ? ` Were the primers already removed from these reads? We recommend uploading primer-trimmed reads: ${platformLabel} primer trimming is early access and still being tuned.`
          : '')
      )
  }
}

/** Button wording for one target of an offered switch; the full sentence is in the message. */
export const getSampleConfigurationSwitchOptionLabel = (
  target: SampleConfigurationTypeKey,
  platform?: SequencingPlatformKey | null
): string => {
  if (!usesPrimersRemovedToggle(platform)) return `Switch to ${getSampleConfigurationLabel(target, platform)}`
  const label = isPrimersRemovedSampleConfiguration(target) ? 'Switch — primers already removed' : 'Switch — primers still present'
  return isRecommendedSampleConfiguration(target, platform) ? `${label} (recommended)` : label
}

/**
 * The way out of a platform issue the submit would refuse with. An override changes the platform the
 * whole order runs on, so it resolves every issue that stems from a wrong detection — except samples
 * that disagree with each other, which one order cannot run at all.
 */
export const getPlatformIssueRemedy = (issue: ValidationIssue): string | null => {
  switch (issue.type) {
    case 'mixed-sequencing-platforms':
      return 'An override does not resolve this: remove the samples of the other platform, or upload them into an order of their own.'
    case 'unsupported-processing-type-for-platform':
      return 'Change the processing type (Edit order), or override the platform if it was detected wrongly.'
    case 'unsupported-sample-configuration-for-platform':
      return 'Change the sample format (Edit order), or override the platform if it was detected wrongly.'
    case 'paired-samples-on-single-end-platform':
      return `Override the platform if these are not ${SequencingPlatforms[issue.platform].label} reads; otherwise upload each sample as one file.`
    default:
      return null
  }
}
